import dayjs, { Dayjs } from 'dayjs';
import { z } from 'zod/v4';

import { integrationSchemaRegistry } from './integration';

export enum TimeBoundaryType {
  Now = 'now',
  Absolute = 'absolute',
  Relative = 'relative',
}

export enum TimePeriodUnit {
  Day = 'day',
  Month = 'month',
  Quarter = 'quarter',
  Year = 'year',
}

export enum TimePeriodAlignment {
  Start = 'start',
  End = 'end',
}

const timeBoundaryNowSchema = z
  .object({
    type: z
      .literal(TimeBoundaryType.Now)
      .register(integrationSchemaRegistry, { i18n: 'timePeriod.boundaryType' }),
  })
  .register(integrationSchemaRegistry, { i18n: 'timePeriod.boundary.now' });

const timeBoundaryAbsoluteSchema = z
  .object({
    type: z
      .literal(TimeBoundaryType.Absolute)
      .register(integrationSchemaRegistry, { i18n: 'timePeriod.boundaryType' }),
    timestamp: z.coerce.date(),
  })
  .register(integrationSchemaRegistry, { i18n: 'timePeriod.boundary.absolute' });

const timeBoundaryRelativeSchema = z
  .object({
    type: z
      .literal(TimeBoundaryType.Relative)
      .register(integrationSchemaRegistry, { i18n: 'timePeriod.boundaryType' }),
    unit: z.enum(TimePeriodUnit).register(integrationSchemaRegistry, { i18n: 'timePeriod.unit' }),
    offset: z.number(),
    alignment: z
      .enum(TimePeriodAlignment)
      .register(integrationSchemaRegistry, { i18n: 'timePeriod.alignment' }),
  })
  .register(integrationSchemaRegistry, { i18n: 'timePeriod.boundary.relative' });

const timeBoundarySchema = z.discriminatedUnion('type', [
  timeBoundaryNowSchema,
  timeBoundaryAbsoluteSchema,
  timeBoundaryRelativeSchema,
]);

export const timePeriodSchema = z
  .object({
    start: timeBoundarySchema,
    end: timeBoundarySchema,
  })
  .register(integrationSchemaRegistry, { component: 'TimePeriod', i18n: 'timePeriod' });

export type TimeBoundaryNow = z.infer<typeof timeBoundaryNowSchema>;
export type TimeBoundaryAbsolute = z.infer<typeof timeBoundaryAbsoluteSchema>;
export type TimeBoundaryRelative = z.infer<typeof timeBoundaryRelativeSchema>;
export type TimeBoundary = z.infer<typeof timeBoundarySchema>;
export type TimePeriod = z.infer<typeof timePeriodSchema>;

export function getTimePeriodDates(
  timePeriod: TimePeriod,
  fromDate = dayjs.utc(),
): [startDate: Dayjs, endDate: Dayjs] {
  return [
    getTimeBoundaryDate(timePeriod.start, fromDate),
    getTimeBoundaryDate(timePeriod.end, fromDate),
  ];
}

export function getTimeBoundaryDate(timeBoundary: TimeBoundary, fromDate: Dayjs) {
  switch (timeBoundary.type) {
    case TimeBoundaryType.Now:
      return fromDate;
    case TimeBoundaryType.Absolute:
      return dayjs.utc(timeBoundary.timestamp);
    case TimeBoundaryType.Relative:
      return getRelativeTimeBoundaryDate(timeBoundary, fromDate);
  }
}

function getRelativeTimeBoundaryDate(
  { unit, offset, alignment }: TimeBoundaryRelative,
  fromDate: Dayjs,
) {
  const offsetDate = fromDate.add(offset, unit);
  return alignment === TimePeriodAlignment.Start
    ? offsetDate.startOf(unit)
    : offsetDate.endOf(unit);
}
